import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { defaultSignUpRegion, loadSignUpRegions, type SignUpRegionChoice } from '../auth/region';
import { deviceTimeZone } from '../domain/day';
import { queryKeys } from '../query/keys';

const regionsQuery = {
  queryKey: queryKeys.signUpRegions(),
  staleTime: 5 * 60_000,
  retry: 1,
  queryFn: loadSignUpRegions,
};

/**
 * The regions open for sign-up and the one to pre-select (`auth/region.ts`), for the sign-in
 * screens. Nothing is fetched until `resolve`, which `startSignIn` calls only for an account
 * that needs a home, so a returning person's sign-in costs no extra request. A fresh answer is
 * reused for five minutes; a stale or failed one is asked for again. `locale` is the device's
 * own (the first of `navigator.languages`, or of `expo-localization`'s locales), not the app's
 * language.
 */
export function useSignUpRegions(locale: string | undefined): {
  resolve: () => Promise<SignUpRegionChoice>;
} {
  const queryClient = useQueryClient();
  const resolve = useCallback(async (): Promise<SignUpRegionChoice> => {
    const open = await queryClient.fetchQuery(regionsQuery);
    return {
      open,
      defaultRegion: open
        ? defaultSignUpRegion({ open, locale, timeZone: deviceTimeZone() })
        : undefined,
    };
  }, [queryClient, locale]);
  return { resolve };
}
